import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatNOK, formatPercent } from "@/lib/format";
import type { LeaderboardEntry } from "@/lib/dashboard-panels-calc";

interface LeaderboardPanelProps {
  entries: LeaderboardEntry[];
  nameOf: (userId: string) => string;
  /** Currently filtered person, highlighted in the list. */
  highlightUserId?: string | null;
  isLoading?: boolean;
}

const MEDAL = ["🥇", "🥈", "🥉"];

/**
 * F2.1 Team Performance leaderboard — per-person delivered BF + target
 * achievement, best first. Top 3 get a medal; the filtered person is
 * highlighted. (Full TV podium is F4.)
 */
export function LeaderboardPanel({ entries, nameOf, highlightUserId, isLoading }: LeaderboardPanelProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Team Performance</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-32 animate-pulse rounded bg-muted" />
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen levert GEEK i perioden.</p>
        ) : (
          <ol className="space-y-1">
            {entries.map((e, i) => (
              <li
                key={e.userId}
                className={cn(
                  "flex items-center justify-between rounded-md px-2 py-1.5 text-sm",
                  e.userId === highlightUserId && "bg-muted",
                )}
              >
                <span className="flex items-center gap-2">
                  <span className="w-6 text-center tabular-nums">{MEDAL[i] ?? i + 1}</span>
                  <span className="font-medium">{nameOf(e.userId)}</span>
                </span>
                <span className="flex items-center gap-3 tabular-nums">
                  <span>{formatNOK(e.bfLevertKr)}</span>
                  {e.achievementPct != null && (
                    <span className="w-14 text-right text-muted-foreground">
                      {formatPercent(e.achievementPct)}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
